/**
 * useEventMutations Hook
 * React Query mutations for event CRUD operations
 * Handles create, update, delete with optimistic updates
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createEvent, updateEvent, deleteEvent } from '../api/calendarApi';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { queryKeys } from 'hooks/config/queryClient';

/**
 * Event mutations hook
 * Provides create, update, and delete mutations with cache invalidation
 *
 * @returns {object} Mutation functions and states
 */
export const useEventMutations = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  // Create event mutation
  const createMutation = useMutation({
    mutationFn: async (eventData) => {
      const result = await createEvent(eventData);
      return result.data;
    },
    onSuccess: (response) => {
      // Invalidate all calendar events queries to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.calendarEvents.all });
    },
    onError: (err) => {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, err?.response?.data || 'Failed to create event');
    },
  });

  // Update event mutation
  const updateMutation = useMutation({
    mutationFn: async ({ eventId, eventData }) => {
      const result = await updateEvent(eventId, eventData);
      return result.data;
    },
    onError: (err, variables, context) => {
      // Rollback on error
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, err?.response?.data || 'Failed to update event');
      if (context?.previousQueries) {
        context.previousQueries.forEach(({ key, data }) => {
          queryClient.setQueryData(key, data);
        });
      }
    },
    onSuccess: (response, variables) => {
      // Invalidate to ensure sync with server
      queryClient.invalidateQueries({ queryKey: queryKeys.calendarEvents.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.calendarEvents.detail(variables?.eventData?.sk) });
    },
  });

  // Delete event mutation
  const deleteMutation = useMutation({
    mutationFn: async (eventId) => {
      const result = await deleteEvent(eventId);
      return result.data;
    },
    onMutate: async (eventId) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['calendar', 'events'] });

      // Snapshot previous values
      const previousQueries = [];
      queryClient.getQueriesData({ queryKey: ['calendar', 'events'] }).forEach(([key, data]) => {
        previousQueries.push({ key, data });
      });

      // Optimistically remove event
      queryClient.setQueriesData({ queryKey: ['calendar', 'events'] }, (old) => {
        if (!old || !Array.isArray(old)) return old;
        return old.filter((event) => event.id !== eventId);
      });

      return { previousQueries };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, err?.response?.data || 'Failed to delete event');
      if (context?.previousQueries) {
        context.previousQueries.forEach(({ key, data }) => {
          queryClient.setQueryData(key, data);
        });
      }
    },
    onSuccess: () => {
      // Invalidate to ensure sync with server
      queryClient.invalidateQueries({ queryKey: ['calendar', 'events'] });
    },
  });

  return {
    // Create
    createEvent: createMutation.mutate,
    createEventAsync: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    createError: createMutation.error,

    // Update
    updateEvent: updateMutation.mutate,
    updateEventAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,

    // Delete
    deleteEvent: deleteMutation.mutate,
    deleteEventAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    deleteError: deleteMutation.error,

    // Combined loading state
    isMutating: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
  };
};

export default useEventMutations;
